import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";

export default function AdminScheduler() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scheduler");
      setJobs(data.jobs || []);
      setRunning(!!data.running);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const runNow = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/scheduler/${id}/run-now`);
      toast.success(`Ran ${id}`);
      reload();
    } catch (e) { toast.error("Run failed"); }
    finally { setBusy(null); }
  };

  if (user && user.system_role !== "platform_admin") {
    return <AppShell><div className="p-8 text-rose-600">Platform admin only.</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-4" data-testid="admin-scheduler-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Scheduled jobs</h1>
            <p className="text-sm text-slate-600">APScheduler · {running ? "running" : "stopped"}</p>
          </div>
          <Badge className={running ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700"}>{running ? "Live" : "Stopped"}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="scheduler-jobs">
          {loading ? <Card><CardContent className="py-6 text-slate-500 text-sm">Loading…</CardContent></Card> :
            jobs.length === 0 ? <Card><CardContent className="py-6 text-slate-500 text-sm">No jobs registered.</CardContent></Card> :
            jobs.map((j) => (
              <Card key={j.job_id} data-testid={`job-${j.job_id}`}>
                <CardHeader><CardTitle className="text-base font-mono">{j.job_id}</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Trigger</span><div className="font-mono text-xs text-slate-700 mt-1">{j.trigger}</div></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Next run</span><div className="text-slate-700">{j.next_run_time || "—"}</div></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last run</span><div className="text-slate-700">{j.last_run_at || "Never"} {j.ok === false && <Badge className="bg-rose-100 text-rose-700 ml-2">Failed</Badge>}{j.ok === true && <Badge className="bg-emerald-100 text-emerald-700 ml-2">OK</Badge>}</div></div>
                  {j.last_result && <div className="text-xs text-slate-500">{j.last_result}</div>}
                  <Button size="sm" variant="outline" onClick={() => runNow(j.job_id)} disabled={busy === j.job_id} data-testid={`run-${j.job_id}-btn`}>
                    {busy === j.job_id ? "Running…" : "Run now"}
                  </Button>
                </CardContent>
              </Card>
            ))
          }
        </div>
      </div>
    </AppShell>
  );
}
